// SPDX-License-Identifier: BUSL-1.1
/**
 * Drizzle ORM schema — all cli.ceo ops tables.
 *
 * NOTE on Better Auth tables: the cli-auth worker (packages/cli-auth/) owns
 * the `user`, `session`, `account`, `verification`, and `jwks` tables. Their
 * schema lives in `packages/cli-auth/src/schema/auth.ts` (authoritative for
 * cli-auth's own queries) and is created by migration
 * `packages/core/drizzle/0014_better_auth_tables.sql` — placed in core's
 * drizzle folder because core's `drizzle/` is the single migration source for
 * the shared Hyperdrive DB. core itself does NOT query those tables. drz-003
 * (migrations must update schema.ts) intentionally does not apply to BA
 * tables. (Migrations are hand-applied per ADR-009 — see
 * `docs-internal/adr/ADR-009-CANONICAL-MIGRATION-STRATEGY.md`.)
 *
 * NOTE on MT-RUN-34 (Phase 0, NocoDB #4721): this file is the source of truth
 * for the migration system. The MT-RUN-33 audit found the migration *files*
 * (replayed onto a fresh DB) did not fully reproduce this schema — the gaps
 * were authoring bugs in 0011/0016 and a never-authored `pr_review_jobs`
 * table. The corrective forward migrations
 * `0027_mt_run_34_org_constraints.sql`,
 * `0028_mt_run_34_moderation_queue_reshape.sql`, and
 * `0029_mt_run_34_schema_replay_reconcile.sql` move the *replay* toward this
 * file (not the other way) — so the table definitions below are unchanged.
 * Verify with `tsx drizzle/scripts/audit-schema-gap.mjs --replay-only`: the
 * in-scope `schema.ts vs REPLAY` diff must be clean.
 */
import { boolean, check, index, integer, jsonb, numeric, pgTable, primaryKey, text, timestamp, unique, uniqueIndex, uuid, } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { opsOrganizations } from "./organizations.js";
// ---------------------------------------------------------------------------
// ops_audit_logs
// ---------------------------------------------------------------------------
export const opsAuditLogs = pgTable("ops_audit_logs", {
    id: uuid("id").notNull().defaultRandom(),
    organizationId: uuid("organization_id"),
    actorId: text("actor_id").notNull(),
    actorType: text("actor_type").notNull(),
    actorIp: text("actor_ip"),
    action: text("action").notNull(),
    resourceType: text("resource_type").notNull(),
    resourceId: text("resource_id").notNull(),
    payload: jsonb("payload").notNull().default({}),
    metadata: jsonb("metadata").default({}),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_audit_logs_pkey" }),
    check("ops_audit_logs_actor_type_check", sql `${t.actorType} IN ('human', 'agent', 'system')`),
    index("ops_audit_logs_actor_id_created_at_idx").on(t.actorId, t.createdAt.desc()),
    index("ops_audit_logs_resource_created_at_idx").on(t.resourceType, t.resourceId, t.createdAt.desc()),
    index("ops_audit_logs_action_created_at_idx").on(t.action, t.createdAt.desc()),
    index("ops_audit_logs_created_at_idx").on(t.createdAt.desc()),
    index("ops_audit_logs_organization_idx").on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_config_versions
// ---------------------------------------------------------------------------
export const opsConfigVersions = pgTable("ops_config_versions", {
    id: uuid("id").notNull().defaultRandom(),
    surfaceKey: text("surface_key").notNull(),
    version: integer("version").notNull(),
    config: jsonb("config").notNull(),
    environment: text("environment").notNull(),
    status: text("status").notNull(),
    createdBy: text("created_by").notNull(),
    comment: text("comment"),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    promotedAt: timestamp("promoted_at", { withTimezone: true }),
    rollbackOf: integer("rollback_of"),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_config_versions_pkey" }),
    uniqueIndex("ops_config_versions_surface_env_version_key").on(t.surfaceKey, t.environment, t.version),
    check("ops_config_versions_status_check", sql `${t.status} IN ('draft', 'staged', 'promoted', 'rolled_back')`),
    index("ops_config_versions_surface_env_status_idx").on(t.surfaceKey, t.environment, t.status),
    index("ops_config_versions_surface_env_version_idx").on(t.surfaceKey, t.environment, t.version.desc()),
]);
// ---------------------------------------------------------------------------
// ops_config_surfaces
// ---------------------------------------------------------------------------
export const opsConfigSurfaces = pgTable("ops_config_surfaces", {
    key: text("key").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    schema: jsonb("schema"),
    environments: text("environments")
        .array()
        .notNull()
        .default(sql `'{production}'`),
    organizationId: uuid("organization_id"),
    // --- CONFIG-LIFE-01: typed-config lifecycle columns ---
    configType: text("config_type").notNull().default("config"),
    lifecycleStage: text("lifecycle_stage").notNull().default("draft"),
    provider: text("provider").default("internal"),
    hypothesis: jsonb("hypothesis"),
    expiryDate: timestamp("expiry_date", { withTimezone: true }),
    rolloutPercentage: integer("rollout_percentage").default(0),
    killReason: text("kill_reason"),
    killedAt: timestamp("killed_at", { withTimezone: true }),
    graduatedAt: timestamp("graduated_at", { withTimezone: true }),
    sunsetAt: timestamp("sunset_at", { withTimezone: true }),
    sunsetPostmortem: text("sunset_postmortem"),
    owner: text("owner"),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.key], name: "ops_config_surfaces_pkey" }),
    check("ops_config_surfaces_config_type_check", sql `${t.configType} IN ('feature_flag', 'kill_switch', 'experiment', 'deployment', 'sdui', 'prompt_template', 'config')`),
    check("ops_config_surfaces_lifecycle_stage_check", sql `${t.lifecycleStage} IN ('hypothesis', 'draft', 'stage', 'rollout', 'decision', 'graduated', 'sunset')`),
    check("ops_config_surfaces_rollout_percentage_check", sql `${t.rolloutPercentage} IS NULL OR (${t.rolloutPercentage} >= 0 AND ${t.rolloutPercentage} <= 100)`),
    index("ops_config_surfaces_config_type_idx").on(t.configType),
    index("ops_config_surfaces_lifecycle_stage_idx").on(t.lifecycleStage),
    index("ops_config_surfaces_provider_idx").on(t.provider),
    index("ops_config_surfaces_expiry_date_idx").on(t.expiryDate),
    index("ops_config_surfaces_organization_idx").on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_emergency_actions
// ---------------------------------------------------------------------------
export const opsEmergencyActions = pgTable("ops_emergency_actions", {
    id: uuid("id").notNull().defaultRandom(),
    actionType: text("action_type").notNull(),
    target: text("target").notNull(),
    previousState: jsonb("previous_state"),
    newState: jsonb("new_state").notNull(),
    reason: text("reason"),
    actorId: text("actor_id").notNull(),
    autoRevertAt: timestamp("auto_revert_at", { withTimezone: true }),
    revertedAt: timestamp("reverted_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_emergency_actions_pkey" }),
    check("ops_emergency_actions_action_type_check", sql `${t.actionType} IN ('kill_switch', 'cache_purge', 'flag_override')`),
    index("ops_emergency_actions_type_created_at_idx").on(t.actionType, t.createdAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_api_keys
// ---------------------------------------------------------------------------
export const opsApiKeys = pgTable("ops_api_keys", {
    id: uuid("id").notNull().defaultRandom(),
    keyHash: text("key_hash").notNull().unique("ops_api_keys_key_hash_key"),
    name: text("name").notNull(),
    role: text("role").notNull().default("admin"),
    organizationId: uuid("organization_id"),
    actorId: text("actor_id").notNull(),
    actorType: text("actor_type").notNull().default("human"),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_api_keys_pkey" }),
    check("ops_api_keys_role_check", sql `${t.role} IN ('super_admin', 'admin', 'viewer', 'reader')`),
    check("ops_api_keys_actor_type_check", sql `${t.actorType} IN ('human', 'agent', 'system')`),
    index("ops_api_keys_hash_active_idx")
        .on(t.keyHash)
        .where(sql `${t.revokedAt} IS NULL`),
    index("ops_api_keys_organization_idx").on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_registration_tokens — Short-lived bootstrap tokens (MT-RUN-07, NocoDB #4582)
// ---------------------------------------------------------------------------
//
// Issued by org admins to onboard new runners. The runner exchanges a
// registration token for a long-lived agent API key via
// `POST /api/runners/register` on first boot. Multi-use within TTL
// (1hr default, 24h max) so an operator can scale a fleet/ASG with one
// mint. Revoke explicitly to kill an outstanding token.
//
// Same hashing model as `ops_api_keys.key_hash` — the plaintext token is
// shown to the operator ONCE at mint time, never persisted. Verification
// in `/api/runners/register` recomputes SHA-256 over the presented token
// and looks up by `token_hash`.
export const opsRegistrationTokens = pgTable("ops_registration_tokens", {
    id: uuid("id").notNull().defaultRandom(),
    organizationId: uuid("organization_id")
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: "cascade" }),
    tokenHash: text("token_hash")
        .notNull()
        .unique("ops_registration_tokens_token_hash_key"),
    scopes: jsonb("scopes").notNull().default(["runner:register"]),
    label: text("label"),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
    useCount: integer("use_count").notNull().default(0),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_registration_tokens_pkey" }),
    check("ops_registration_tokens_expires_check", sql `${t.expiresAt} > ${t.createdAt}`),
    check("ops_registration_tokens_use_count_check", sql `${t.useCount} >= 0`),
    // Active-token lookup: org admins listing live tokens / revoke flow.
    // Partial index excludes revoked rows so the index stays tight as
    // history accumulates.
    index("ops_registration_tokens_org_active_idx")
        .on(t.organizationId, t.expiresAt.desc())
        .where(sql `${t.revokedAt} IS NULL`),
]);
// ---------------------------------------------------------------------------
// ops_admin_users
// ---------------------------------------------------------------------------
export const opsAdminUsers = pgTable("ops_admin_users", {
    id: uuid("id").notNull().defaultRandom(),
    authUserId: text("auth_user_id")
        .notNull()
        .unique("ops_admin_users_auth_user_id_key"),
    email: text("email").notNull(),
    displayName: text("display_name"),
    role: text("role").notNull().default("viewer"),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_admin_users_pkey" }),
    check("ops_admin_users_role_check", sql `${t.role} IN ('super_admin', 'admin', 'viewer')`),
    index("ops_admin_users_active_auth_user_id_idx")
        .on(t.authUserId)
        .where(sql `${t.isActive} = true`),
]);
// ---------------------------------------------------------------------------
// ops_agent_sessions — FOUNDATION-01 (ADR-004)
// ---------------------------------------------------------------------------
//
// Persistent record of every governed Anthropic Managed Agents session.
// Written by GovernedSessionManager on create + terminate (single chokepoint,
// per ADR-004 Decision 1). HTTP routes that bypass governance do NOT write
// here — that's intentional, to keep audit attribution clean.
//
// ma_status  = verbatim MA transport state (idle/running/rescheduling/terminated).
// outcome    = governance-derived settlement reason. Nullable while session is
//              still active. MA does not distinguish success from failure in the
//              transport layer — the distinction lives here.
//
// Cost is snapshotted at persist time using the canonical calculateSessionCost
// from @cli-ceo/shared-constants. Rollups (DASH-19) can recompute from the raw
// token columns if model rates change retroactively.
//
// metadata jsonb stores project namespacing (DOGFOOD-01 Pattern 2 —
// `metadata->>'project' = 'dd'`) and is extensible for fleet_id, team_id,
// policy_version without migrations. GIN-indexed for containment queries.
export const opsAgentSessions = pgTable("ops_agent_sessions", {
    // Identity
    id: text("id").notNull(), // Anthropic MA session ID (stable)
    organizationId: uuid("organization_id"),
    agentId: text("agent_id"), // nullable for ad-hoc sessions
    actorId: text("actor_id").notNull(), // join key → ops_api_keys.actor_id (no FK — ops_api_keys.actor_id is not UNIQUE yet)
    // Model + status
    model: text("model").notNull(),
    maStatus: text("ma_status").notNull(),
    outcome: text("outcome"), // null while active
    // Timing
    startedAt: timestamp("started_at", { withTimezone: true }).notNull(),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    activeSeconds: integer("active_seconds").notNull().default(0),
    durationSeconds: integer("duration_seconds").notNull().default(0),
    // Usage snapshot
    inputTokens: integer("input_tokens").notNull().default(0),
    outputTokens: integer("output_tokens").notNull().default(0),
    cacheReadTokens: integer("cache_read_tokens").notNull().default(0),
    cacheCreation5mTokens: integer("cache_creation_5m_tokens")
        .notNull()
        .default(0),
    cacheCreation1hTokens: integer("cache_creation_1h_tokens")
        .notNull()
        .default(0),
    webSearchRequests: integer("web_search_requests").notNull().default(0),
    // Cost (USD, snapshot at persist time)
    totalCostUsd: numeric("total_cost_usd", { precision: 10, scale: 6 })
        .notNull()
        .default("0"),
    // Governance context
    budgetScope: text("budget_scope"),
    budgetLimitUsd: numeric("budget_limit_usd", { precision: 10, scale: 6 }),
    // Extensibility — DOGFOOD-01 project namespacing + future dimensions
    metadata: jsonb("metadata").notNull().default({}),
    // Timestamps
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_agent_sessions_pkey" }),
    check("ops_agent_sessions_ma_status_check", sql `${t.maStatus} IN ('idle', 'running', 'rescheduling', 'terminated')`),
    check("ops_agent_sessions_outcome_check", sql `${t.outcome} IS NULL OR ${t.outcome} IN ('success', 'budget_exceeded', 'manual_terminate', 'policy_denied', 'error')`),
    check("ops_agent_sessions_outcome_consistency_check", sql `${t.outcome} IS NULL OR ${t.maStatus} = 'terminated'`),
    // Dashboard time-series (DASH-19 rollup hot path) — partial skips active sessions
    index("ops_agent_sessions_completed_at_idx")
        .on(t.completedAt.desc())
        .where(sql `${t.completedAt} IS NOT NULL`),
    // "My sessions" / per-actor billing
    index("ops_agent_sessions_actor_completed_at_idx").on(t.actorId, t.completedAt.desc()),
    // Model-level cost aggregation (DASH-19/21)
    index("ops_agent_sessions_model_completed_at_idx").on(t.model, t.completedAt.desc()),
    // Active sessions lookup (FOUNDATION-02 reconciliation sweeper) — partial
    index("ops_agent_sessions_ma_status_idx")
        .on(t.maStatus)
        .where(sql `${t.maStatus} != 'terminated'`),
    // Project filtering per DOGFOOD-01 (`metadata @> '{"project":"dd"}'`)
    index("ops_agent_sessions_metadata_gin_idx").using("gin", t.metadata),
    index("ops_agent_sessions_organization_idx").on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_moderation_queue — CEO-MOD-01
// ---------------------------------------------------------------------------
//
// Persistent record of every governed tool call that hit a `manual` policy
// gate and is awaiting (or has received) human approval. Written by
// `DrizzleModerationStore.enqueue` (called from GovernedSessionManager when a
// tool confirmation routes through the `manual` gate) and updated by
// `DrizzleModerationStore.resolve` from `POST /api/moderation/:id/resolve`.
//
// IMPORTANT: resolving a row here only persists the moderator decision. The
// agent-side tool confirmation is delivered separately by GovernedSessionManager
// when it next polls the queue — keeping HTTP routes free of transport coupling.
//
// `tool_input` is denormalised JSONB for forensic display in the moderation
// inbox UI; `policy_gate` records which gate fired so the dashboard can show
// gate-level distributions without joining adapter metadata.
export const opsModerationQueue = pgTable("ops_moderation_queue", {
    id: uuid("id").notNull().defaultRandom(),
    organizationId: uuid("organization_id"),
    sessionId: text("session_id").notNull(),
    toolUseId: text("tool_use_id").notNull(),
    toolName: text("tool_name").notNull(),
    toolInput: jsonb("tool_input").notNull(),
    policyGate: text("policy_gate").notNull(),
    status: text("status").notNull().default("pending"),
    sessionThreadId: text("session_thread_id"),
    denyMessage: text("deny_message"),
    resolvedBy: text("resolved_by"),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_moderation_queue_pkey" }),
    check("ops_moderation_queue_policy_gate_check", sql `${t.policyGate} IN ('safe', 'guarded', 'manual')`),
    check("ops_moderation_queue_status_check", sql `${t.status} IN ('pending', 'approved', 'rejected')`),
    // Inbox / badge: pending items newest-first
    index("ops_moderation_queue_status_created_at_idx").on(t.status, t.createdAt.desc()),
    // Per-session lookup (governance manager polls here on resume)
    index("ops_moderation_queue_session_id_idx").on(t.sessionId),
    index("ops_moderation_queue_organization_idx").on(t.organizationId),
]);
// Re-export organizations and memberships from separate file
// (split to comply with line count limits)
export { opsOrganizations, } from './organizations.js';
export { opsOrgMemberships, } from './organizations.js';
// ---------------------------------------------------------------------------
// ops_org_invitations — Org membership invitations (CEO-V3, Task #4360)
// ---------------------------------------------------------------------------
export const opsOrgInvitations = pgTable('ops_org_invitations', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    email: text('email').notNull(),
    role: text('role').notNull().default('viewer'),
    invitedBy: text('invited_by').notNull(),
    token: text('token').notNull().unique(),
    status: text('status').notNull().default('pending'),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    check('ops_org_invitations_status_check', sql `${t.status} IN ('pending', 'accepted', 'expired', 'revoked')`),
    index('ops_org_invitations_organization_id_idx').on(t.organizationId),
    index('ops_org_invitations_token_idx').on(t.token),
    index('ops_org_invitations_email_idx').on(t.email),
]);
// ---------------------------------------------------------------------------
// ops_budget_usage — Budget tracking per (organization_id, scope) (Task #4271)
//
// MT-RUN-23 (NocoDB #4648): organization_id added so the same `scope` key
// (operator id, factory id, fleet slug) can carry separate budget state per
// tenant. Identity is enforced via the compound UNIQUE NULLS NOT DISTINCT
// constraint declared below (replaces the prior single-column PK on `scope`).
// Migration 0026 ships the SQL. Column is nullable to admit legacy rows
// during the backfill window; new writes set it from admin context.
// ---------------------------------------------------------------------------
export const opsBudgetUsage = pgTable("ops_budget_usage", {
    organizationId: uuid("organization_id").references(() => opsOrganizations.id, { onDelete: "cascade" }),
    scope: text("scope").notNull(),
    totalBudgetUsd: numeric("total_budget_usd", { precision: 10, scale: 2 }),
    spentUsd: numeric("spent_usd", { precision: 10, scale: 6 })
        .notNull()
        .default("0"),
    reservedUsd: numeric("reserved_usd", { precision: 10, scale: 6 })
        .notNull()
        .default("0"),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    // MT-RUN-23: identity is (organization_id, scope), NULLS NOT DISTINCT so
    // the legacy NULL-org backfill lane stays enforced. Matches the SQL
    // shipped by migration 0026.
    unique("ops_budget_usage_org_scope_key").on(t.organizationId, t.scope).nullsNotDistinct(),
    index("ops_budget_usage_total_budget_idx").on(t.totalBudgetUsd.desc()),
    index("ops_budget_usage_organization_id_idx").on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_pi_dev_sessions — pi.dev agent session tracking
// ---------------------------------------------------------------------------
export const opsPiDevSessions = pgTable("ops_pi_dev_sessions", {
    id: uuid("id").notNull().defaultRandom(),
    sessionId: text("session_id").notNull().unique(),
    agentId: text("agent_id").notNull(),
    status: text("status").notNull().default("active"),
    startedAt: timestamp("started_at", { withTimezone: true }).notNull(),
    endedAt: timestamp("ended_at", { withTimezone: true }),
    durationSeconds: integer("duration_seconds"),
    inputTokens: integer("input_tokens").notNull().default(0),
    outputTokens: integer("output_tokens").notNull().default(0),
    cost: numeric("cost", { precision: 10, scale: 6 }).notNull().default("0"),
    toolCallCount: integer("tool_call_count").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_pi_dev_sessions_pkey" }),
    check("ops_pi_dev_sessions_status_check", sql `${t.status} IN ('active', 'completed', 'terminated')`),
    index("ops_pi_dev_sessions_agent_id_idx").on(t.agentId),
    index("ops_pi_dev_sessions_status_idx").on(t.status),
    index("ops_pi_dev_sessions_started_at_idx").on(t.startedAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_pi_dev_tool_calls — pi.dev tool call audit
// ---------------------------------------------------------------------------
export const opsPiDevToolCalls = pgTable("ops_pi_dev_tool_calls", {
    id: uuid("id").notNull().defaultRandom(),
    sessionId: text("session_id").notNull(),
    tool: text("tool").notNull(),
    toolName: text("tool_name").notNull(),
    arguments: jsonb("arguments"),
    result: jsonb("result"),
    status: text("status").notNull(),
    policyGate: text("policy_gate").notNull().default("safe"),
    approvedBy: text("approved_by"),
    blockedBy: text("blocked_by"),
    durationMs: integer("duration_ms").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_pi_dev_tool_calls_pkey" }),
    check("ops_pi_dev_tool_calls_tool_check", sql `${t.tool} IN ('read', 'write', 'edit', 'bash', 'mcp', 'custom')`),
    check("ops_pi_dev_tool_calls_status_check", sql `${t.status} IN ('success', 'error', 'blocked')`),
    check("ops_pi_dev_tool_calls_policy_gate_check", sql `${t.policyGate} IN ('safe', 'guarded', 'manual')`),
    index("ops_pi_dev_tool_calls_session_id_idx").on(t.sessionId),
    index("ops_pi_dev_tool_calls_tool_idx").on(t.tool),
    index("ops_pi_dev_tool_calls_status_idx").on(t.status),
    index("ops_pi_dev_tool_calls_created_at_idx").on(t.createdAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_agents — Framework-agnostic agent registry (PR-GOV-01)
// ---------------------------------------------------------------------------
export const opsAgents = pgTable("ops_agents", {
    id: uuid("id").notNull().defaultRandom(),
    // Uniqueness is scoped per-org via the compound `ops_agents_org_agent_id_key`
    // constraint below (MT-RUN-04, NocoDB #4579). Two orgs may legitimately
    // register an agent under the same operator-chosen agent_id.
    agentId: text("agent_id").notNull(),
    organizationId: uuid("organization_id"),
    sdk: text("sdk").notNull(),
    name: text("name").notNull(),
    version: text("version"),
    status: text("status").notNull().default("idle"),
    policy: text("policy").notNull().default("guarded"),
    budgetAction: text("budget_action").notNull().default("warn"),
    budgetMonthly: numeric("budget_monthly", { precision: 10, scale: 2 }),
    budgetPerSession: numeric("budget_per_session", { precision: 10, scale: 2 }),
    budgetSpentThisMonth: numeric("budget_spent_this_month", { precision: 10, scale: 2 }).default("0"),
    config: jsonb("config").default({}),
    webhookUrl: text("webhook_url"),
    webhookSecret: text("webhook_secret"),
    triggers: jsonb("triggers").default([]),
    source: text("source"),
    sourceUrl: text("source_url"),
    metadata: jsonb("metadata").default({}),
    /**
     * Self-reported runtime attestation from the agent process (PIO-31).
     * Populated on every heartbeat. Nullable so runners on pre-PIO-31
     * code keep working — they send no attestation, this stays NULL.
     *
     * Shape (mirrors RunnerAttestation in cli-ceo-agents):
     *   {
     *     repoSha:             string | null,   // git rev-parse HEAD on the agent box
     *     repoOrigin:          string | null,   // remote.origin.url
     *     capabilitiesVersion: string,          // contract version (e.g., 'v1')
     *     secretsHash:         string | null,   // sha256(salt|tokens) — never raw
     *     bootedAtMs:          number,          // when the runner started
     *     nodeVersion:         string,          // process.version
     *   }
     *
     * Drift = (attestation->>'repoSha') != latest cli-ceo-agents/main HEAD.
     * Indexed by (attestation->>'repoSha') for fast fleet-wide drift queries.
     */
    attestation: jsonb("attestation"),
    capabilities: jsonb("capabilities").default({
        sessions: false,
        jobs: false,
        budget: true,
        policy: true,
        rateLimit: false,
        approval: false,
    }),
    rateLimit: jsonb("rate_limit"),
    rateLimitUsed: integer("rate_limit_used").default(0),
    rateLimitWindowStart: timestamp("rate_limit_window_start", { withTimezone: true }),
    lastHeartbeat: timestamp("last_heartbeat", { withTimezone: true }),
    /**
     * Default signing-key kid used by the agent when signing heartbeats
     * (HB-05). NULL = agent never created a signing key (heartbeats land
     * unsigned, accepted unless HEARTBEAT_REQUIRE_SIGNATURE='true'). The
     * full keyring lives in ops_agent_signing_keys; this column is just
     * the convenience pointer to "the most recently created, not-yet-revoked
     * key" so the dashboard can render it without joining.
     */
    signingKeyKid: text("signing_key_kid"),
    /**
     * Pull-verification callback URL (HB-06). When set, the challenge-issuer
     * cron POSTs a signed nonce here every 5 min and expects the agent to
     * sign it back via /api/agents/:agentId/challenge-response. NULL = agent
     * isn't pull-verifiable; rows still get inserted with verdict='no_callback'.
     */
    callbackUrl: text("callback_url"),
    /**
     * Set true after 3 consecutive non-pass challenge results (failed | timeout
     * | no_callback). Cleared on the next pass. Surfaced in the UI as a red
     * banner so moderators see integrity warnings without grepping the
     * challenge-results table.
     */
    challengeFailing: boolean("challenge_failing").notNull().default(false),
    /** Timestamp of the most recent challenge attempt (regardless of verdict). */
    lastChallengeAt: timestamp("last_challenge_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    check("ops_agents_sdk_check", sql `${t.sdk} IN ('pi', 'claude-code', 'anthropic-ma', 'aider', 'custom')`),
    check("ops_agents_status_check", sql `${t.status} IN ('idle', 'running', 'paused', 'terminated')`),
    check("ops_agents_policy_check", sql `${t.policy} IN ('safe', 'guarded', 'manual')`),
    check("ops_agents_budget_action_check", sql `${t.budgetAction} IN ('warn', 'block', 'terminate')`),
    // Compound uniqueness scoping (MT-RUN-04, NocoDB #4579).
    //
    // Replaces the prior global UNIQUE on agent_id with a per-org constraint.
    // NULLS NOT DISTINCT means rows with NULL organization_id (legacy / pre
    // multi-tenancy) still cannot share an agent_id — preserving the old
    // safety net for un-migrated rows. Postgres ≥15 required.
    unique("ops_agents_org_agent_id_key")
        .on(t.organizationId, t.agentId)
        .nullsNotDistinct(),
    index("ops_agents_sdk_idx").on(t.sdk),
    index("ops_agents_status_idx").on(t.status),
    index("ops_agents_last_heartbeat_idx").on(t.lastHeartbeat),
    index("ops_agents_organization_idx").on(t.organizationId),
]);
// ---------------------------------------------------------------------------
// ops_agent_signing_keys — Per-agent HMAC keys for heartbeat auth (HB-05, NocoDB #4406)
// ---------------------------------------------------------------------------
/**
 * HMAC shared secrets used by `heartbeat-signature` middleware to verify
 * the X-Heartbeat-Signature header on POST /heartbeat and POST /activity.
 *
 * Threat model: server DB compromise lets an attacker forge heartbeats
 * for any agent with a non-revoked key. Mitigation v1 is DB-level
 * encryption-at-rest + role-based access; future migration to
 * asymmetric (Ed25519) would give true non-repudiation. v1 is a
 * meaningful upgrade over the prior "anyone with agent_id can forge"
 * baseline.
 *
 * Lifecycle:
 *   - POST /api/agents/:agentId/keys        → mint a new kid, return secret ONCE
 *   - DELETE /api/agents/:agentId/keys/:kid → revoke (sets revoked_at; row stays for audit)
 *
 * Verification path: middleware loads the row by (agent_id, kid) where
 * revoked_at IS NULL, recomputes HMAC-SHA256 over the canonical request
 * string (timestamp + body), and constant-time-compares with the
 * provided X-Heartbeat-Signature.
 */
export const opsAgentSigningKeys = pgTable("ops_agent_signing_keys", {
    agentId: text("agent_id").notNull(),
    kid: text("kid").notNull(),
    /**
     * HMAC shared secret. Stored as base64url-encoded random bytes. The
     * column name is `secret` rather than `secret_hash` because we cannot
     * hash-and-throw-away an HMAC key — verification needs the original
     * material. See the table comment for the threat model.
     */
    secret: text("secret").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
}, (t) => [
    primaryKey({
        columns: [t.agentId, t.kid],
        name: "ops_agent_signing_keys_pkey",
    }),
    index("ops_agent_signing_keys_active_idx")
        .on(t.agentId, t.kid)
        .where(sql `${t.revokedAt} IS NULL`),
]);
// ---------------------------------------------------------------------------
// ops_agent_jobs — Framework-agnostic job queue (PR-GOV-01)
// ---------------------------------------------------------------------------
/**
 * MT-RUN-49 (#4742, migration 0030) — `agent_id` semantic shift.
 * See docs-internal/adr/ADR-010-TENANT-AWARE-WEBHOOK-DISPATCH.md.
 *
 * Tenant-aware dispatch keys on `(organization_id, agent_type)`, NOT on a
 * literal `agent_id`. As a consequence the meaning of `agent_id` on this
 * table changes:
 *
 *   - OLD meaning: the dispatch key. The webhook handler wrote the
 *     capability string ('pr-reviewer' etc.) here, and the claim CTE
 *     matched `WHERE agent_id = <capability>`.
 *   - NEW meaning: the *claimed-by* runner identity. On a webhook-origin
 *     row it is still written as the `agent_type` string at enqueue time
 *     (so `NOT NULL` holds and the legacy claim path keeps working during
 *     the transition), and the claim CTE OVERWRITES it with the claiming
 *     runner's real `agent_id` when the row is claimed.
 *
 * `agent_id` stays `text NOT NULL` for 0030 — it is made nullable, and the
 * enqueue-time write is dropped, in the MT-RUN-42 cleanup task (its own
 * migration). 0030 is purely additive.
 *
 * `agent_type` is the capability/queue the job targets — the real dispatch
 * key. `ops_agent_jobs_dedup_v2_key` is the multi-tenant-correct idempotency
 * scope; the v1 `ops_agent_jobs_dedup_key` is kept through the transition and
 * dropped in the cleanup task.
 */
export const opsAgentJobs = pgTable("ops_agent_jobs", {
    id: text("id").notNull().primaryKey(),
    /**
     * MT-RUN-49: claimed-by runner identity (see table comment). NOT the
     * dispatch key — `agent_type` is. Stays `NOT NULL` for 0030; becomes
     * nullable in the MT-RUN-42 cleanup migration.
     */
    agentId: text("agent_id").notNull(),
    /**
     * MT-RUN-49: the capability/queue the job targets — the dispatch key.
     * Domain matches the webhook's `SUPPORTED_AGENTS`. `DEFAULT 'pr-reviewer'`
     * backfills every existing row (today every webhook-origin row has
     * `agent_id ∈ SUPPORTED_AGENTS`); migration 0030 additionally runs an
     * explicit backfill `UPDATE … SET agent_type = agent_id` for exactness.
     */
    agentType: text("agent_type").notNull().default("pr-reviewer"),
    organizationId: uuid("organization_id"),
    trigger: text("trigger").notNull(),
    triggerSource: text("trigger_source"),
    triggerEventId: text("trigger_event_id"),
    status: text("status").notNull().default("pending"),
    inputPayload: jsonb("input_payload").default({}),
    outputPayload: jsonb("output_payload").default({}),
    error: text("error"),
    cost: numeric("cost", { precision: 10, scale: 6 }),
    sessionId: text("session_id"),
    /**
     * CEO-V4 Phase 0 (NocoDB #4769) — pi-extension session correlation.
     * Optional foreign-style link to the pi-extension session that executed
     * this job (a `pi` runner PATCHes this on session start). Nullable: not
     * every job has a pi-session (legacy jobs, non-pi runners). Indexed for
     * the operator-visibility "active agents" grid which joins jobs ↔ live
     * pi sessions.
     */
    piSessionId: text("pi_session_id"),
    priority: integer("priority").default(0),
    maxRetries: integer("max_retries").default(3),
    retryCount: integer("retry_count").default(0),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    startedAt: timestamp("started_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
}, (t) => [
    check("ops_agent_jobs_trigger_check", sql `${t.trigger} IN ('webhook', 'api', 'cron', 'manual', 'schedule')`),
    // MT-RUN-49 (0030): `'routing_failed'` added to the set — the terminal
    // state for a job that sat `pending` past its TTL with no eligible
    // runner for its `(organization_id, agent_type)` (ADR-010 R2).
    check("ops_agent_jobs_status_check", sql `${t.status} IN ('pending', 'processing', 'completed', 'failed', 'skipped', 'cancelled', 'routing_failed')`),
    // MT-RUN-49 (0030): `agent_type` domain — the webhook's SUPPORTED_AGENTS.
    // Extending this set as new capabilities ship is itself a forward migration.
    check("ops_agent_jobs_agent_type_check", sql `${t.agentType} IN ('pr-reviewer', 'code-reviewer', 'security-scanner')`),
    index("ops_agent_jobs_status_idx").on(t.status, t.createdAt),
    index("ops_agent_jobs_agent_status_idx").on(t.agentId, t.status, t.createdAt),
    index("ops_agent_jobs_organization_idx").on(t.organizationId),
    // MT-RUN-49 (0030): the new claim hot path. The claim CTE's Path B
    // matches `WHERE organization_id = $org AND agent_type ∈ caps`; this
    // index serves it. The existing `ops_agent_jobs_agent_status_idx` stays
    // for the legacy (Path A) claim path during the transition.
    index("ops_agent_jobs_dispatch_idx").on(t.organizationId, t.agentType, t.status, t.createdAt),
    // CEO-V4 Phase 0 (NocoDB #4769): pi-extension session correlation. Serves
    // the operator-visibility "active agents" grid which filters jobs by the
    // live pi-session id when joining ops_agent_jobs ↔ pi session activity.
    index("ops_agent_jobs_pi_session_idx").on(t.piSessionId),
    // Idempotency scope (MT-RUN-04 follow-on, NocoDB #4579).
    //
    // (agent_id, trigger_source, trigger_event_id) uniqueness, with NULLS
    // NOT DISTINCT so a NULL trigger_source or trigger_event_id still
    // collides — preventing duplicate "manual / no-event-id" runs from
    // the same agent slipping through under multi-tenancy. Postgres ≥15.
    //
    // MT-RUN-49 (0030): KEPT through the transition (see dedup_v2 below).
    // Dropped in the MT-RUN-42 cleanup task once no in-flight code relies
    // on it — NOT dropped here. (Dropping it is MT-RUN-57.)
    unique("ops_agent_jobs_dedup_key")
        .on(t.agentId, t.triggerSource, t.triggerEventId)
        .nullsNotDistinct(),
    // MT-RUN-49 (0030): multi-tenant-correct idempotency scope. The v1 key
    // above was correct only while `agent_id` *was* the capability string.
    // Once `agent_id` becomes claimed-by-runner-identity, re-delivery dedup
    // must key on `(organization_id, agent_type, …)` so (a) it survives the
    // claim-time `agent_id` overwrite and (b) two tenants' jobs with a
    // coincidentally-equal `trigger_event_id` do not dedup against each
    // other. NULLS NOT DISTINCT, Postgres ≥15.
    unique("ops_agent_jobs_dedup_v2_key")
        .on(t.organizationId, t.agentType, t.triggerSource, t.triggerEventId)
        .nullsNotDistinct(),
]);
// ---------------------------------------------------------------------------
// ops_agent_capabilities — runner job-type capability advertisements
// (MT-RUN-49, NocoDB #4742, migration 0030)
// ---------------------------------------------------------------------------
/**
 * Which job *types* (capabilities) a runner can serve. The claim CTE's new
 * (Path B) dispatch matches `WHERE organization_id = $org AND
 * EXISTS (… ops_agent_capabilities … capability = j.agent_type)` — see
 * docs-internal/adr/ADR-010-TENANT-AWARE-WEBHOOK-DISPATCH.md.
 *
 * Why a normalized table, not a JSONB column on `ops_agents`:
 *   - `ops_agents.capabilities` is a fixed boolean shape (`{sessions, jobs,
 *     budget, …}`) describing *transport* capabilities — a different concept
 *     from *job-type* capabilities. `ops_agents.triggers` is an untyped array.
 *     Reusing either would force a `jsonb` containment operator into the
 *     `FOR UPDATE SKIP LOCKED` claim CTE — the hot-path destabilization
 *     ADR-010 explicitly designs against. A narrow table gives the claim CTE
 *     a plain indexed `EXISTS`/`IN`.
 *   - It is the natural home for *heterogeneous* runners (one runner
 *     advertising both `pr-reviewer` and `security-scanner`).
 *
 * `organization_id` is DENORMALIZED here (copied from `ops_agents` at write
 * time) so the claim CTE filters capability + org without a join back to
 * `ops_agents`. There is intentionally NO foreign key: `agent_id` is not
 * unique in `ops_agents` (it is unique only per-org, `UNIQUE (organization_id,
 * agent_id)` — MT-RUN-04), so an FK on `agent_id` alone is not expressible.
 * `ON DELETE` cleanup is handled in the registration / de-registration code
 * paths (code-level cascade), asserted by tests — same pattern as
 * `ops_agent_sessions.actor_id`.
 *
 * Populated by the runner-register flow and `POST /api/agents` (MT-RUN-52).
 */
export const opsAgentCapabilities = pgTable("ops_agent_capabilities", {
    agentId: text("agent_id").notNull(),
    /**
     * Denormalized from `ops_agents.organization_id` at write time. Nullable
     * to mirror `ops_agents` (legacy / super-admin agents may have a NULL
     * org). No FK — see the table comment.
     */
    organizationId: uuid("organization_id"),
    capability: text("capability").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({
        columns: [t.agentId, t.capability],
        name: "ops_agent_capabilities_pkey",
    }),
    check("ops_agent_capabilities_capability_check", sql `${t.capability} IN ('pr-reviewer', 'code-reviewer', 'security-scanner')`),
    // Claim-time filter: "which of this org's runners serve capability X".
    index("ops_agent_capabilities_org_capability_idx").on(t.organizationId, t.capability),
]);
// ---------------------------------------------------------------------------
// DEPRECATED: ops_pi_dev_agents — Use ops_agents instead
// ---------------------------------------------------------------------------
export const opsPiDevAgents = pgTable("ops_pi_dev_agents", {
    id: uuid("id").notNull().defaultRandom(),
    agentId: text("agent_id").notNull().unique(),
    name: text("name").notNull(),
    source: text("source").notNull(),
    systemPrompt: text("system_prompt"),
    model: text("model"),
    status: text("status").notNull().default("idle"),
    policy: text("policy").notNull().default("guarded"),
    budgetMonthly: numeric("budget_monthly", { precision: 10, scale: 2 }),
    budgetPerSession: numeric("budget_per_session", { precision: 10, scale: 2 }),
    budgetAction: text("budget_action").notNull().default("warn"),
    environment: jsonb("environment"),
    extensions: text("extensions").array(),
    metadata: jsonb("metadata"),
    lastHeartbeat: timestamp("last_heartbeat", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_pi_dev_agents_pkey" }),
    check("ops_pi_dev_agents_status_check", sql `${t.status} IN ('idle', 'running', 'paused', 'terminated')`),
    check("ops_pi_dev_agents_policy_check", sql `${t.policy} IN ('safe', 'guarded', 'manual')`),
    check("ops_pi_dev_agents_budget_action_check", sql `${t.budgetAction} IN ('warn', 'block', 'terminate')`),
    index("ops_pi_dev_agents_status_idx").on(t.status),
    index("ops_pi_dev_agents_last_heartbeat_idx").on(t.lastHeartbeat.desc()),
]);
// ---------------------------------------------------------------------------
// DEPRECATED: pr_review_jobs — Use ops_agent_jobs instead
// ---------------------------------------------------------------------------
export const prReviewJobs = pgTable("pr_review_jobs", {
    id: text("id").notNull().primaryKey(),
    agentId: text("agent_id").notNull().default("pr-reviewer"),
    prNumber: integer("pr_number").notNull(),
    repo: text("repo").notNull(),
    action: text("action").notNull(),
    sender: text("sender").notNull(),
    cloneUrl: text("clone_url").notNull(),
    headRef: text("head_ref").notNull(),
    baseRef: text("base_ref").notNull(),
    title: text("title").notNull(),
    body: text("body"),
    author: text("author").notNull(),
    trigger: text("trigger").notNull().default("pr_event"),
    status: text("status").notNull().default("pending"),
    error: text("error"),
    result: text("result"),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    startedAt: timestamp("started_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
}, (t) => [
    check("pr_review_jobs_status_check", sql `${t.status} IN ('pending', 'processing', 'completed', 'failed', 'skipped')`),
    index("pr_review_jobs_status_idx").on(t.status, t.createdAt),
    index("pr_review_jobs_agent_idx").on(t.agentId, t.createdAt),
]);
// ---------------------------------------------------------------------------
// ops_vcs_repositories — VCS repository to organization mapping (CEO-V3)
// ---------------------------------------------------------------------------
/**
 * Maps VCS repositories to organizations for webhook multi-tenancy.
 * Used by webhook handler to resolve organization context from repository info.
 */
export const opsVcsRepositories = pgTable('ops_vcs_repositories', {
    id: uuid('id').notNull().defaultRandom(),
    organizationId: uuid('organization_id').notNull().references(() => opsOrganizations.id, { onDelete: 'cascade' }),
    provider: text('provider').notNull(), // 'github', 'gitlab', etc.
    owner: text('owner').notNull(), // repo owner/user
    repo: text('repo').notNull(), // repo name
    fullName: text('full_name').notNull(), // 'owner/repo'
    isDefault: boolean('is_default').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: 'ops_vcs_repositories_pkey' }),
    check('ops_vcs_repositories_provider_check', sql `${t.provider} IN ('github', 'gitlab', 'bitbucket', 'gitea', 'forgejo')`),
    uniqueIndex('ops_vcs_repositories_unique_idx').on(t.provider, t.owner, t.repo),
    index('ops_vcs_repositories_organization_idx').on(t.organizationId),
    index('ops_vcs_repositories_full_name_idx').on(t.fullName),
]);
// ---------------------------------------------------------------------------
// ops_agent_heartbeat_events — Append-only heartbeat audit log (HB-01, NocoDB #4402)
// ---------------------------------------------------------------------------
/**
 * Append-only event log for agent heartbeats. Replaces the prior last-write-wins
 * pattern where ops_agents.metadata.lastHeartbeatMeta was overwritten on every
 * POST. This table is the source of truth for governance audit trails:
 * "show me every heartbeat from agent X in the last 7 days", "reconstruct
 * state before incident Y", etc.
 *
 * ops_agents.lastHeartbeat is preserved as a denormalized fast-read shortcut
 * for dashboard list views; this table holds the full history.
 *
 * signature / signatureKid: reserved for HB-05 (HMAC signed heartbeats); NULL
 * until HEARTBEAT_REQUIRE_SIGNATURE strict mode is enabled.
 *
 * source: invocation source vocabulary populated by agent SDKs in HB-07
 * (Paperclip-derived: timer | assignment | on_demand | automation | unknown).
 */
export const opsAgentHeartbeatEvents = pgTable("ops_agent_heartbeat_events", {
    id: uuid("id").notNull().defaultRandom(),
    agentId: text("agent_id").notNull(),
    occurredAt: timestamp("occurred_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    source: text("source").notNull().default("unknown"),
    status: text("status").notNull(),
    payload: jsonb("payload").notNull().default({}),
    signature: text("signature"),
    signatureKid: text("signature_kid"),
}, (t) => [
    primaryKey({
        columns: [t.id],
        name: "ops_agent_heartbeat_events_pkey",
    }),
    check("ops_agent_heartbeat_events_source_check", sql `${t.source} IN ('timer', 'assignment', 'on_demand', 'automation', 'unknown')`),
    check("ops_agent_heartbeat_events_status_check", sql `${t.status} IN ('idle', 'running', 'paused', 'terminated')`),
    index("ops_agent_heartbeat_events_agent_time_idx").on(t.agentId, t.occurredAt.desc()),
]);
// ---------------------------------------------------------------------------
// ops_agent_activity — Evidence-of-work events (HB-03, NocoDB #4404)
// ---------------------------------------------------------------------------
/**
 * Lightweight evidence stream that complements ops_agent_heartbeat_events.
 * A heartbeat proves connectivity; an activity row proves productive work.
 *
 * The silent-run watchdog (HB-04) queries this table to flag agents that
 * are pinging fine but have stopped producing output. Moderators see the
 * raw stream on the Activity tab of the agent detail page.
 *
 * Each kind has a distinct payload shape (validated by Zod at the route
 * level — see agent-activity.ts):
 *   tool_call         — { tool, args_summary, result_size?, ms? }
 *   artifact          — { type, uri, title? }
 *   decision          — { summary, rationale?, refs? }
 *   context_compressed — { before_tokens, after_tokens, ratio }
 *   blocker           — { kind, detail }
 *   task_started      — { task_id, title? }
 *   task_completed    — { task_id, outcome? }
 *
 * signature / signatureKid reserved for HB-05; same conventions as
 * ops_agent_heartbeat_events.
 */
export const opsAgentActivity = pgTable("ops_agent_activity", {
    id: uuid("id").notNull().defaultRandom(),
    agentId: text("agent_id").notNull(),
    sessionId: text("session_id"),
    occurredAt: timestamp("occurred_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    kind: text("kind").notNull(),
    payload: jsonb("payload").notNull().default({}),
    signature: text("signature"),
    signatureKid: text("signature_kid"),
    /**
     * Set when the blocker-alert sweep has fanned out a notification for
     * this row (Slack webhook / email). NULL means "not yet processed".
     * Only `kind='blocker'` rows are ever inspected by the sweep — for any
     * other kind the column stays NULL forever, which is fine. A partial
     * index covers the hot query path.
     *
     * Migration 0034 (CEO-V4 follow-on, Leo "quickest follow-on ship").
     */
    alertedAt: timestamp("alerted_at", { withTimezone: true }),
}, (t) => [
    primaryKey({
        columns: [t.id],
        name: "ops_agent_activity_pkey",
    }),
    check("ops_agent_activity_kind_check", sql `${t.kind} IN ('tool_call', 'artifact', 'decision', 'context_compressed', 'blocker', 'task_started', 'task_completed')`),
    index("ops_agent_activity_agent_time_idx").on(t.agentId, t.occurredAt.desc()),
    index("ops_agent_activity_kind_idx").on(t.agentId, t.kind, t.occurredAt.desc()),
    // Partial index for the blocker-alert sweep — only the un-alerted
    // blockers are ever scanned. Massively smaller footprint than indexing
    // every row's alerted_at.
    index("ops_agent_activity_blocker_unalerted_idx")
        .on(t.occurredAt)
        .where(sql `${t.kind} = 'blocker' AND ${t.alertedAt} IS NULL`),
]);
// ---------------------------------------------------------------------------
// ops_agent_challenge_results — Pull-verification audit log (HB-06, NocoDB #4407)
// ---------------------------------------------------------------------------
/**
 * Records one row per challenge round issued by the challenge-issuer cron.
 *
 * Lifecycle: cron generates a nonce, inserts a row with verdict='timeout'
 * (the worst-case default), then POSTs to ops_agents.callback_url. If the
 * agent responds via POST /api/agents/:agentId/challenge-response within
 * the freshness window, the row is updated to verdict='passed' or 'failed'
 * with responded_at set. If no response arrives, the row stays as 'timeout'.
 * If callback_url was NULL when the cron fired, verdict is 'no_callback'
 * (no outbound HTTP attempted).
 *
 * Three consecutive non-pass verdicts → ops_agents.challenge_failing=true.
 *
 * Why a separate table from ops_agent_activity: pull verification is
 * server-initiated integrity evidence, not agent-emitted work evidence.
 * Mixing them would muddy the activity stream the silent-run watchdog
 * relies on.
 */
export const opsAgentChallengeResults = pgTable("ops_agent_challenge_results", {
    id: uuid("id").notNull().defaultRandom(),
    agentId: text("agent_id").notNull(),
    challengedAt: timestamp("challenged_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    nonce: text("nonce").notNull(),
    respondedAt: timestamp("responded_at", { withTimezone: true }),
    verdict: text("verdict").notNull(),
    /** Optional human-readable reason for failed/timeout verdicts. */
    reason: text("reason"),
}, (t) => [
    primaryKey({
        columns: [t.id],
        name: "ops_agent_challenge_results_pkey",
    }),
    check("ops_agent_challenge_results_verdict_check", sql `${t.verdict} IN ('passed', 'failed', 'timeout', 'no_callback')`),
    index("ops_agent_challenge_agent_time_idx").on(t.agentId, t.challengedAt.desc()),
    // Unique index on (agent_id, nonce) keeps replay attempts on a known
    // nonce from creating duplicate rows or being re-claimed.
    uniqueIndex("ops_agent_challenge_results_nonce_idx").on(t.agentId, t.nonce),
]);
// ---------------------------------------------------------------------------
// ops_vcs_repositories — Repository-to-Organization Mapping (CEO-V3 Phase 2)
// ---------------------------------------------------------------------------
//# sourceMappingURL=schema.js.map